import { useReactions } from '@gik/api/stream/useStream';
import { useUser } from '@gik/api/users/user';
import { SignInFlowCalloutBlock } from '@gik/auth/components/SignInFlow/SignInFlowCallout';
import { SignInFlowContentCopyVariant } from '@gik/auth/components/SignInFlow/SignInFlowStartContent';
import { signin } from '@gik/auth/utils/auth';
import type { User } from '@gik/core/models/gik/User';
import { useUserStore } from '@gik/core/store/UserStore';
import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import { translationKeys } from '@gik/stream/i18n/en';
import type { FlatActivity, Reaction, StreamComment } from '@gik/stream/types/Stream';
import type { StreamParticipant } from '@gik/stream/types/StreamParticipant';
import { getReactionIconName } from '@gik/stream/utils/findUserReaction';
import { renderReactionIcon } from '@gik/stream/utils/reactions';
import ChatAlt2Icon from '@heroicons/react/outline/ChatAlt2Icon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import useLatest from 'react-use/lib/useLatest';
import { Button } from '../../../ui/Button';
import type { ReactionEmojiKey } from '../../../ui/OpenMoji';
import { ReactionEmojis } from '../../../ui/OpenMoji';
import { SvgIcon } from '../../../ui/SvgIcon';
import { StreamComments } from '../Comments/StreamComments';
import type { onDeleteReactionCallback, onReactionCallback } from '../Reactions/ReactionButton';
import { ReactionButton } from '../Reactions/ReactionButton';

export type StreamReactionsContextProps = {
  participants: StreamParticipant[];
  commentParticipants: StreamParticipant[];
  replyingId?: string;
  replyingUserId?: string;
  replyMessage?: string;
  setReplyMessage?: (v: string) => void;
  setReplyingId?: (v: string) => void;
  setReplyingUserId?: (v: string) => void;
};

export const StreamReactionsContext = React.createContext<StreamReactionsContextProps>(null);

export type StreamReactionsProps = {
  activity: FlatActivity;
  participants?: StreamParticipant[];
  isPageOwnerOrOrganizer?: boolean;
  disabled?: boolean;
  highlightedCommentId?: string;
  onDeleteComment?: (id: string) => void;
  onAddComment?: (forId: string, value: string) => void;
  onReaction?: (forId: string, name: ReactionEmojiKey) => void;
  onDeleteReaction?: (forId: string, name: ReactionEmojiKey) => void;
  onRetryCreate?: () => void;
} & UIComponent;

function StreamReactionsComp({
  children,
  className,
  activity,
  participants,
  isPageOwnerOrOrganizer,
  disabled,
  highlightedCommentId,
  onAddComment,
  onDeleteComment,
  onReaction,
  onDeleteReaction,
  onRetryCreate,
  ...otherProps
}: React.PropsWithChildren<StreamReactionsProps>): React.ReactElement {
  const bem = useBemCN('stream-reactions');
  const { t } = useTranslation();

  const userId = useUserStore(state => state.id);
  const routeId = useInkindStore(store => store.inkindRouteId);

  const [commentParticipants, setCommentParticipants] = React.useState<StreamParticipant[]>();
  const latestCommentParticipants = useLatest<StreamParticipant[]>(commentParticipants);
  const [replyingId, setReplyingId] = React.useState<string>();
  const [replyingUserId, setReplyingUserId] = React.useState<string>();
  const [replyMessage, setReplyMessage] = React.useState<string>();

  const initialState: StreamReactionsContextProps = {
    participants,
    commentParticipants,
    replyingId,
    replyingUserId,
    setReplyingId,
    setReplyingUserId,
    replyMessage,
    setReplyMessage,
  };

  const { data: userReactions, mutate: mutateUserReactions } = useReactions('user', userId, { allPages: true });

  const [selected, setSelected] = React.useState<ReactionEmojiKey>(undefined);
  const [reactionCounts, setReactionCounts] = React.useState<Record<string, number>>(activity.reaction_counts);
  const [commentsCount, setCommentsCount] = React.useState<number>(reactionCounts?.comment);
  const [loadingProfileId, setLoadingProfileId] = React.useState<string>();
  const [userProfileData, setUserProfileData] = React.useState<Record<string, User>>({});
  const latestUserProfileData = useLatest<Record<string, User>>(userProfileData);

  const selectedReaction = userReactions?.find(r => r.activity_id === activity.id && !r.parent && r.kind !== 'comment');
  const selectedReactionId = selectedReaction?.id;

  React.useEffect(() => {
    if (!selected && selectedReaction) {
      setSelected(getReactionIconName(selectedReaction));
    }
  }, [selected, selectedReaction, activity]);

  // const [allComments, setAllComments] = React.useState<Reaction<StreamComment>[]>();
  // const latestAllComments = useLatest<Reaction<StreamComment>[]>(allComments);

  const { data: loadedProfile, isValidating: profileIsValidating } = useUser(loadingProfileId);

  const handleReaction: onReactionCallback = React.useCallback(
    async (forId: string, name: ReactionEmojiKey) => {
      if (!userId) {
        const userId = await signin({
          copyVariant: !routeId ? SignInFlowContentCopyVariant.GENERAL : SignInFlowContentCopyVariant.INKIND_PAGE,
          callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
          routeId,
        });
        if (!userId) {
          return;
        }
      }

      setSelected(name);

      // optimistically update reaction counters
      const newReactionCounts = JSON.parse(JSON.stringify(reactionCounts));
      // remove user's old reaction
      if (selected && selected !== name) {
        newReactionCounts[selected] = newReactionCounts[selected] - 1;
      }
      // add the new reaction the user has selected
      newReactionCounts[name] = (newReactionCounts[name] || 0) + 1;
      setReactionCounts(newReactionCounts);
    },
    [userId, reactionCounts, selected, routeId]
  );

  const handleReactionSuccess = React.useCallback(
    (forId: string, name: ReactionEmojiKey) => {
      mutateUserReactions();
      return onReaction?.(forId, name);
    },
    [onReaction, mutateUserReactions]
  );

  const handleDeleteReaction: onDeleteReactionCallback = React.useCallback(async () => {
    if (!userId) {
      const userId = await signin({
        copyVariant: !routeId ? SignInFlowContentCopyVariant.GENERAL : SignInFlowContentCopyVariant.INKIND_PAGE,
        callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
        routeId,
      });
    }

    mutateUserReactions(
      userReactions.filter(reaction => reaction !== selectedReaction),
      false
    );
    setSelected(null);

    // remove the reaction the user currently has selected
    const newReactionCounts = JSON.parse(JSON.stringify(reactionCounts));
    newReactionCounts[selected] = newReactionCounts[selected] - 1;
    setReactionCounts(newReactionCounts);
  }, [reactionCounts, userId, selected, selectedReaction, userReactions, mutateUserReactions, routeId]);

  const handleDeleteReactionSuccess = React.useCallback(
    (forId: string, name: ReactionEmojiKey) => {
      mutateUserReactions();
      return onDeleteReaction?.(forId, name);
    },
    [onDeleteReaction, mutateUserReactions]
  );

  const handleCommentsClick = React.useCallback(async () => {
    if (!userId) {
      const userId = await signin({
        copyVariant: !routeId ? SignInFlowContentCopyVariant.GENERAL : SignInFlowContentCopyVariant.INKIND_PAGE,
        callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
        routeId,
      });
      if (!userId) {
        return;
      }
    }

    document.getElementById(`${activity.id}-input`).focus();
  }, [activity, userId, routeId]);

  function renderReactions(forId: string, reaction_counts: Record<string, number>) {
    const likeReactionEntries = reaction_counts
      ? Object.entries(reaction_counts).filter(entry => Object.keys(ReactionEmojis).indexOf(entry[0]) >= 0)
      : [];
    if (!likeReactionEntries.length) return <div {...bem('reactions')} key={'empty'} />;

    const totalReactions = likeReactionEntries.map(entry => entry[1]).reduce((prev, current) => prev + current);

    return (
      <div key={forId} {...bem('reactions')}>
        {likeReactionEntries
          .sort((a, b) => b[1] - a[1]) // show most chosen emoji first
          .slice(0, 2)
          .map((entry, index) => {
            if (entry[1] === 0) return null;
            return <div key={`${entry[0]}-${index}`}>{renderReactionIcon(entry[0] as ReactionEmojiKey, true)}</div>;
          })}

        {totalReactions > 0 && likeReactionEntries.length > 0 && (
          <div {...bem('reactions-total')} key="total">
            {totalReactions}
          </div>
        )}
      </div>
    );
  }

  const getParticipantsFromComments = React.useCallback(
    (items: Reaction<StreamComment>[], userProfileData: Record<string, User>) => {
      const participants: StreamParticipant[] = [];
      // get all user suggestions from the comments
      items?.forEach(item => {
        // skip if user already exists in participants
        if (participants.find(p => p.id === item.data.userId)) return;

        // skip if user was already added and has correct profile data
        if (commentParticipants?.find(p => p.id === item.data.userId && p.display !== item.data.userId)) return;

        const userProfile = userProfileData[item.data.userId as string];

        participants.push({
          id: item.data.userId as string,
          display: userProfile?.fullName || (item.data.userId as string),
          userAvatarThumbSmallURL: userProfile?.avatarThumbSmallURL,
          userAvatarThumbMediumURL: userProfile?.avatarThumbMediumURL,
        });
      });

      return participants;
    },
    [commentParticipants]
  );

  const handleChange = React.useCallback(
    (_items: Reaction<StreamComment>[], total: number) => {
      if (!_items || !_items.length) return;
      let newParticipants = getParticipantsFromComments(_items, latestUserProfileData.current);

      // filter out participants that were found in comments but are already part of the page organizer participants
      newParticipants = newParticipants.filter(user => !participants.find(p => p.id === user.id));

      const allParticipants = newParticipants;

      setCommentParticipants(allParticipants);
      setCommentsCount(total);
      // setAllComments(_items);
    },
    [participants, latestUserProfileData, setCommentParticipants, getParticipantsFromComments]
  );

  const handleSubCommentsChange = React.useCallback(
    (_items: Reaction<StreamComment>[]) => {
      if (!_items || !_items.length) return;
      let newParticipants = getParticipantsFromComments(_items, latestUserProfileData.current);

      // filter out participants that were found in comments but are already part of the page organizer participants
      newParticipants = newParticipants.filter(user => !participants.find(p => p.id === user.id));
      newParticipants = newParticipants.filter(user => !latestCommentParticipants.current.find(p => p.id === user.id));

      const allParticipants = (latestCommentParticipants.current || []).concat(newParticipants);

      setCommentParticipants(allParticipants);
    },
    [
      participants,
      latestUserProfileData,
      latestCommentParticipants,
      setCommentParticipants,
      getParticipantsFromComments,
    ]
  );

  const loadMoreParticipantProfiles = React.useCallback(() => {
    const missingParticipants = commentParticipants?.filter(participant => participant.display === participant.id);
    if (missingParticipants.length > 0) {
      setLoadingProfileId(missingParticipants[0].id);
    }
  }, [commentParticipants]);

  /**
   * populate userProfileData
   */
  React.useEffect(() => {
    if (profileIsValidating || !loadedProfile || !loadingProfileId) return;

    const userProfileData = latestUserProfileData.current;

    if (userProfileData[loadingProfileId]) return;

    const newUserProfileData = JSON.parse(JSON.stringify(latestUserProfileData.current));
    newUserProfileData[loadingProfileId] = loadedProfile;
    setUserProfileData(newUserProfileData);

    const allParticipants = latestCommentParticipants.current || [];

    const match = allParticipants.find(p => p.id === loadingProfileId);
    if (match) match.display = loadedProfile.fullName;

    setCommentParticipants(allParticipants);
  }, [profileIsValidating, loadedProfile, loadingProfileId, latestCommentParticipants, latestUserProfileData]);

  /**
   * Load user profile data when comment participants populate
   */
  React.useEffect(() => {
    const hasMissingData =
      commentParticipants?.findIndex(participant => participant.userAvatarThumbSmallURL === undefined) > -1;

    if (hasMissingData) {
      loadMoreParticipantProfiles();
    }
  }, [commentParticipants, loadMoreParticipantProfiles]);

  React.useEffect(() => {
    setReactionCounts(activity.reaction_counts);
  }, [activity]);

  let latest_comments: Reaction<StreamComment>[] = [];

  if (activity.latest_reactions?.comment || activity.latest_children) {
    // TODO: fix typing
    // eslint-disable-next-line
    latest_comments = activity.latest_reactions?.comment || (activity.latest_children as any)?.comment;
  }

  return (
    <StreamReactionsContext.Provider value={initialState}>
      <div {...bem(null, null, className)} {...otherProps}>
        <div {...bem('reactions-wrapper')}>
          <div {...bem('comments')}>{t(translationKeys.commentCount, { count: commentsCount || 0 }).toString()}</div>
          {renderReactions(activity.id, reactionCounts)}
        </div>

        <div {...bem('actions')}>
          <ReactionButton
            forId={activity.id}
            disabled={disabled}
            hasIcon
            onReaction={handleReaction}
            onReactionSuccess={handleReactionSuccess}
            onDeleteReaction={handleDeleteReaction}
            onDeleteReactionSuccess={handleDeleteReactionSuccess}
            selected={selected}
            selectedReactionId={selectedReactionId}
            onClick={async ev => {
              if (disabled) return;
              if (!userId) {
                ev.stopPropagation();

                const userId = await signin({
                  copyVariant: !routeId
                    ? SignInFlowContentCopyVariant.GENERAL
                    : SignInFlowContentCopyVariant.INKIND_PAGE,
                  callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
                  routeId,
                });
              }
            }}
          />

          <Button
            noFocusStyling
            variant="white"
            prepend={<SvgIcon Icon={ChatAlt2Icon} />}
            uppercase={false}
            onClick={handleCommentsClick}
            disabled={disabled}
          >
            Comment
          </Button>
        </div>

        <div {...bem('comments-wrapper')}>
          <StreamComments
            disabled={disabled}
            forId={activity.id}
            highlightedId={highlightedCommentId}
            initialTotal={activity.reaction_counts ? activity.reaction_counts?.comment || 0 : undefined}
            latest_comments={latest_comments}
            isPageOwnerOrOrganizer={isPageOwnerOrOrganizer}
            onAddComment={onAddComment}
            onReaction={onReaction}
            onDeleteComment={onDeleteComment}
            onDeleteReaction={onDeleteReaction}
            onChange={handleChange}
            onSubCommentsChange={handleSubCommentsChange}
            inputAtTop
          />
        </div>
      </div>
    </StreamReactionsContext.Provider>
  );
}

export const StreamReactions = withComponentErrorBoundary(StreamReactionsComp);
